<template>
  <div class="page-content-wrapper">
    <div class="card rounded-3 shadow mt-4 border-1">
      <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center">
        <h5 class="mb-0 fw-bold">Service Extracts</h5>
      </div>

      <div class="card-body">
        <!-- Loading -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Feature flag disabled -->
        <div v-else-if="!featureFlagEnabled" class="text-center text-muted py-4 text-xs">
          This feature is not available.
        </div>

        <!-- Error -->
        <div v-else-if="errorMessage" class="alert alert-danger text-xs" role="alert">
          {{ errorMessage }}
        </div>

        <!-- Empty state -->
        <div v-else-if="jobs.length === 0" class="text-center text-muted py-4 text-xs">
          No service extract jobs found.
        </div>

        <!-- Jobs table -->
        <div v-else class="table-responsive">
          <table class="table table-bordered table-hover align-middle text-xs mb-0">
            <thead class="table-light">
              <tr>
                <th>Status</th>
                <th>File Name</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="job in jobs" :key="job.id">
                <td>
                  <span class="badge rounded-pill" :class="statusBadgeClass(job.status)">
                    {{ job.status }}
                  </span>
                </td>
                <td>{{ job.file_name || '—' }}</td>
                <td>{{ formatDate(job.created_at) }}</td>
                <td>{{ formatDate(job.updated_at) }}</td>
                <td>
                  <button
                    v-if="job.status === 'completed' && job.download_url"
                    class="btn btn-sm btn-outline-primary"
                    style="font-size: 0.75rem; white-space: nowrap"
                    @click="downloadExtract(job)"
                  >
                    Download Extract
                  </button>
                  <span v-else>—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API_BASE_URL } from "@/config";

export default {
  name: "ServiceExtracts",
  data() {
    return {
      loading: false,
      errorMessage: "",
      jobs: [],
      featureFlagEnabled: false,
    };
  },
  methods: {
    statusBadgeClass(status) {
      const map = {
        completed: "bg-success",
        pending: "bg-secondary",
        failed: "bg-danger",
        processing: "bg-warning text-dark",
      };
      return map[status] || "bg-secondary";
    },
    formatDate(isoDate) {
      if (!isoDate) return "—";
      const d = new Date(isoDate);
      return d.toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formatApplicationIds(ids) {
      if (!ids || ids.length === 0) return "—";
      return ids.join(", ");
    },
    downloadExtract(job) {
      if (!job.download_url) return;
      const link = document.createElement("a");
      link.href = job.download_url;
      link.setAttribute("download", job.file_name || "extract.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async fetchFeatureFlags() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/verify_api/system_management_api/feature-flags/`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );
        if (!response.ok) return;
        const data = await response.json();
        const flags = data.flags || [];
        this.featureFlagEnabled = flags.some(
          (flag) => flag.name === "service_extract" && flag.active
        );
      } catch {
        this.featureFlagEnabled = false;
      }
    },
    async fetchJobs() {
      const token = localStorage.getItem("token");
      this.loading = true;
      this.errorMessage = "";

      try {
        const endpoint = `${API_BASE_URL}/verify_api/service_extract/jobs/`;
        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${token}`,
          },
        });

        const data = await response.json();

        if (!response.ok || data.status !== "success") {
          this.errorMessage = data.error || data.message || "Failed to load service extract jobs.";
          return;
        }

        this.jobs = Array.isArray(data.jobs) ? data.jobs : [];
      } catch (error) {
        this.errorMessage = "Failed to load service extract jobs.";
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.loading = true;
    await this.fetchFeatureFlags();
    if (this.featureFlagEnabled) {
      await this.fetchJobs();
    } else {
      this.loading = false;
    }
  },
};
</script>
